// pages/RuleEdit.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PageHeader from "components/PageLayout";
import { ArrowLeft, Save, Plus, Trash2 } from "lucide-react";
import { useRuleByID } from "hooks/useRuleById";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import { useToast } from "components/Toast";

export default function RuleEdit() {
  const { ruleId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const companyId = useEffectiveCompanyId();

  const { rules, loading, error } = useRuleByID(ruleId);
  const latest = rules?.[0];

  const [routing, setRouting] = useState([{ mid: "", allocation: 0 }]);
  const [constraints, setConstraints] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (latest) {
      setRouting(latest.routing ?? [{ mid: "", allocation: 0 }]);
      setConstraints(latest.constraints ?? []);
    }
  }, [latest]);

  // --- Constraint handlers
  function handleConstraintChange(index, field, value) {
    setConstraints((prev) =>
      prev.map((c, i) => (i === index ? { ...c, [field]: value } : c))
    );
  }

  function addConstraintRow() {
    setConstraints((prev) => [...prev, { type: "CAMPAIGN", value: "" }]);
  }

  function removeConstraintRow(index) {
    setConstraints((prev) => prev.filter((_, i) => i !== index));
  }

  async function handleSave() {
    setSaving(true);
    try {
      const payload = {
        scope: latest.scope, // 🚫 scope locked
        constraints: constraints.length > 0 ? constraints : undefined,
        routing: routing.map((r) => ({
          mid: r.mid,
          allocation: Number(r.allocation),
        })),
        status: latest?.status ?? "Live",
      };

      const res = await makeApiRequest(
        "put",
        apiRoutes.editRule(ruleId),
        { id: user.id, accessToken: user.accessToken },
        companyId,
        payload
      );

      toast({
        status: "success",
        title: "Rule updated",
        description: `A new version (v${res.data.version}) has been created.`,
        action: {
          label: "View rule",
          onClick: () => navigate(`/rules/${ruleId}`),
        },
      });

      navigate(`/rules/${ruleId}`);
    } catch (err) {
      console.error("[RuleEdit] Failed to save rule", err);
      toast({
        status: "destructive",
        title: "Failed to update rule",
        description: "Something went wrong. Please try again.",
      });
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p>Loading...</p>;
  if (error) return <p className="text-red-600">Failed to load rule.</p>;

  return (
    <div>
      <PageHeader
        title="Edit Rule"
        subtitle={`Editing ${latest?.scope?.type} – ${latest?.scope?.value}`}
        actions={
          <div className="flex gap-2">
            <button className="btn btn-ghost" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4" />
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              <Save className="h-4 w-4" />
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        }
      />

      <div className="container py-6 space-y-6">
        {/* Scope (read-only) */}
        <section className="surface rounded-xl ring-1 ring-border p-6 space-y-4">
          <h2 className="text-lg font-semibold">Scope</h2>
          <p className="text-muted-foreground text-sm">
            Scope is locked. To change BIN or issuer, create a new rule.
          </p>
          <input
            type="text"
            className="input input-bordered w-full bg-muted"
            value={latest?.scope?.value ?? ""}
            disabled
          />
        </section>

        {/* Constraints (editable) */}
        <section className="surface rounded-xl ring-1 ring-border p-6 space-y-4">
          <h2 className="text-lg font-semibold">Constraints</h2>
          <p className="text-sm text-muted-foreground">
            Adjust constraints for this rule (e.g. Campaign).
          </p>

          {constraints.map((row, i) => (
            <div key={i} className="flex gap-2 items-center">
              <select
                className="select select-bordered w-40"
                value={row.type}
                onChange={(e) =>
                  handleConstraintChange(i, "type", e.target.value)
                }
              >
                <option value="CAMPAIGN">Campaign</option>
              </select>
              <input
                type="text"
                className="input input-bordered flex-1"
                placeholder="Constraint value (e.g. CAMP123)"
                value={row.value}
                onChange={(e) =>
                  handleConstraintChange(i, "value", e.target.value)
                }
              />
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => removeConstraintRow(i)}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}

          <button
            type="button"
            className="btn btn-ghost inline-flex items-center gap-2"
            onClick={addConstraintRow}
          >
            <Plus className="h-4 w-4" />
            Add Constraint
          </button>
        </section>

        {/* Routing (editable) */}
        <section className="surface rounded-xl ring-1 ring-border p-6 space-y-4">
          <h2 className="text-lg font-semibold">Routing</h2>
          {routing.map((r, i) => (
            <div key={i} className="flex gap-2 items-center">
              <input
                type="text"
                className="input input-bordered flex-1"
                placeholder="MID"
                value={r.mid}
                onChange={(e) =>
                  setRouting((prev) =>
                    prev.map((x, idx) =>
                      idx === i ? { ...x, mid: e.target.value } : x
                    )
                  )
                }
              />
              <input
                type="number"
                className="input input-bordered w-24"
                placeholder="%"
                value={r.allocation}
                onChange={(e) =>
                  setRouting((prev) =>
                    prev.map((x, idx) =>
                      idx === i
                        ? { ...x, allocation: Number(e.target.value) }
                        : x
                    )
                  )
                }
              />
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() =>
                  setRouting((prev) => prev.filter((_, idx) => idx !== i))
                }
              >
                Remove
              </button>
            </div>
          ))}
          <button
            type="button"
            className="btn btn-secondary mt-2"
            onClick={() =>
              setRouting((prev) => [...prev, { mid: "", allocation: 0 }])
            }
          >
            Add MID
          </button>
        </section>
      </div>
    </div>
  );
}
